import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { X } from 'lucide-react';
import type { S3Tone } from './visual';
import { isTopLayer, popLayer, pushLayer } from './layerStack';
import { supportRootClass, useSupportLook } from '../v4/lookContext';

export const S3Badge: React.FC<{ tone: S3Tone; small?: boolean; title?: string; children: React.ReactNode }> = ({
  tone, small, title, children,
}) => (
  <span className={`s3-badge s3-badge--${tone}${small ? ' s3-badge--sm' : ''}`} title={title}>{children}</span>
);

/**
 * L'inline porta SOLO la misura (`--s3-av`): larghezza, altezza e corpo del
 * testo sono calcolati dalla classe `.s3-avatar` in support-v3.css, cosi la
 * proporzione iniziali/diametro resta una decisione del foglio di stile.
 *
 * Con `src` il cerchio ospita la foto reale (associato o rappresentante): stesso
 * contenitore, stessa misura, nessun cambio di layout. Le iniziali restano il
 * fallback quando la foto manca o non si carica (`onError`), cosi un file
 * cancellato non lascia mai un buco.
 */
export const S3Avatar: React.FC<{
  initials: string; size: number; tone?: 'green' | 'chip' | 'amber'; title?: string; src?: string;
}> = ({ initials, size, tone = 'green', title, src }) => {
  const [broken, setBroken] = React.useState(false);
  // Cambiando foto (altro ticket, altro assegnatario) si riparte dal tentativo.
  React.useEffect(() => { setBroken(false); }, [src]);
  const showPhoto = Boolean(src) && !broken;
  return (
    <span
      className={`s3-avatar${tone === 'chip' ? ' s3-avatar--chip' : ''}${tone === 'amber' ? ' s3-avatar--amber' : ''}${showPhoto ? ' s3-avatar--photo' : ''}`}
      style={{ ['--s3-av' as string]: `${size}px` } as React.CSSProperties}
      title={title}
    >
      {showPhoto ? (
        <img src={src} alt={title ? `Foto di ${title}` : ''} loading="lazy" onError={() => setBroken(true)} />
      ) : (
        initials
      )}
    </span>
  );
};

export const S3EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="s3-empty">{children}</div>
);

/** Voce di menu: serve a garantire il `role` che `S3Menu` usa per la tastiera. */
export const S3MenuItem: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }
> = ({ danger, className = '', children, ...rest }) => (
  <button
    type="button"
    role="menuitem"
    className={`s3-menuitem${danger ? ' s3-menuitem--danger' : ''}${className ? ` ${className}` : ''}`}
    {...rest}
  >
    {children}
  </button>
);

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Registra il layer nello stack condiviso finche resta aperto (Escape + scroll lock). */
const useLayer = (open: boolean, lockScroll: boolean): LayerHandle => {
  const id = useMemo(() => Symbol('s3-layer'), []);
  useEffect(() => {
    if (!open) return;
    pushLayer(id, lockScroll);
    return () => popLayer(id);
  }, [open, id, lockScroll]);
  return useMemo(() => ({ isTop: () => isTopLayer(id) }), [id]);
};

interface LayerHandle { isTop: () => boolean }

/** Escape che agisce solo sul layer piu in alto (stack condiviso, layerStack.ts). */
const useEscape = (open: boolean, layer: LayerHandle, onClose: () => void) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !layer.isTop()) return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, layer, onClose]);
};

/**
 * Focus dentro il pannello all'apertura (sull'elemento marcato `data-autofocus`
 * se c'e, altrimenti sul primo focalizzabile), ritorno al nodo precedente alla chiusura.
 */
const useFocusReturn = (open: boolean, panel: React.RefObject<HTMLElement | null>) => {
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const node = panel.current;
    if (node) {
      const first = node.querySelector<HTMLElement>('[data-autofocus]') || node.querySelector<HTMLElement>(FOCUSABLE);
      (first || node).focus({ preventScroll: true });
    }
    return () => previous?.focus?.({ preventScroll: true });
  }, [open, panel]);
};

/**
 * Tab ciclico dentro il pannello. La trappola si disattiva quando il pannello
 * NON e il layer piu in alto: con un cassetto aperto e una modale sopra (es.
 * "Trasferisci" aperta dalle Proprieta nel cassetto) entrambi registrano il
 * loro handler in fase di cattura, e quello del cassetto — registrato per
 * primo — rubava il Tab alla modale. Vince sempre il layer superiore.
 */
const useFocusTrap = (open: boolean, panel: React.RefObject<HTMLElement | null>, layer: LayerHandle) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !layer.isTop()) return;
      const node = panel.current;
      if (!node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) { e.preventDefault(); node.focus({ preventScroll: true }); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!e.shiftKey && (active === last || !node.contains(active))) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && (active === first || !node.contains(active))) { e.preventDefault(); last.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, panel, layer]);
};

/**
 * Dropdown ancorato: chiusura con Escape (solo se e il layer piu in alto) e
 * con click fuori (prototipo L441-L442). Con `asMenu` (default) il contenitore
 * espone `role="menu"` e la navigazione da tastiera fra le `S3MenuItem`:
 * frecce su/giu, Home/End, focus sulla prima voce all'apertura. Se il
 * contenuto NON e fatto di menuitem, passare `asMenu={false}`: niente
 * `role="menu"` e niente gestione frecce; con un nome accessibile
 * (`ariaLabel`/`labelledBy`) il pannello si annuncia come `role="dialog"`.
 *
 * `triggerRef`: il bottone che apre il pannello. Va passato SEMPRE quando il
 * trigger fa da toggle, altrimenti il mousedown su di esso chiude il menu e il
 * click successivo lo riapre — e il pannello non si chiuderebbe mai.
 */
export const S3Menu: React.FC<{
  open: boolean; onClose: () => void; anchor?: 'down' | 'up'; asMenu?: boolean;
  ariaLabel?: string; labelledBy?: string; triggerRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}> = ({ open, onClose, anchor = 'down', asMenu = true, ariaLabel, labelledBy, triggerRef, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const layer = useLayer(open, false);
  useEscape(open, layer, onClose);

  const items = useCallback(
    () => Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') || []),
    []
  );

  useEffect(() => {
    if (!open) return;
    const node = ref.current;
    const previous = document.activeElement as HTMLElement | null;
    if (asMenu) items()[0]?.focus({ preventScroll: true });
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef?.current?.contains(target)) return; // il toggle si chiude da solo
      if (node && !node.contains(target)) onClose();
    };
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
      // Il focus torna all'elemento che ha aperto il menu solo se era finito
      // dentro il menu: altrimenti si ruberebbe un focus spostato altrove.
      if (asMenu && node?.contains(document.activeElement)) previous?.focus?.({ preventScroll: true });
    };
  }, [open, onClose, asMenu, items, triggerRef]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!asMenu) return;
    const list = items();
    if (list.length === 0) return;
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); list[(i + 1 + list.length) % list.length].focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); list[(i - 1 + list.length) % list.length].focus(); }
    else if (e.key === 'Home') { e.preventDefault(); list[0].focus(); }
    else if (e.key === 'End') { e.preventDefault(); list[list.length - 1].focus(); }
  };

  if (!open) return null;
  return (
    <div
      ref={ref}
      className="s3-menu"
      style={anchor === 'up' ? { right: 0, bottom: 34 } : { right: 0, top: 40 }}
      role={asMenu ? 'menu' : ariaLabel || labelledBy ? 'dialog' : undefined}
      aria-label={ariaLabel}
      aria-labelledby={labelledBy}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
};

/**
 * Cassetto laterale del pannello Contesto (ruling 2; sempre attivo lato
 * rappresentante). `title` e obbligatorio: e il nome accessibile dell'aside.
 * `className` / `headClassName` (facoltative, v4): classi in piu sull'aside e
 * sulla riga titolo, per agganciare la pelle a una classe invece che alla
 * struttura; senza, il DOM e quello di sempre.
 */
export const S3Drawer: React.FC<{
  open: boolean; onClose: () => void; title: string; children: React.ReactNode;
  className?: string; headClassName?: string;
}> = ({ open, onClose, title, children, className, headClassName }) => {
  const panel = useRef<HTMLElement>(null);
  const layer = useLayer(open, true);
  useEscape(open, layer, onClose);
  useFocusReturn(open, panel);
  useFocusTrap(open, panel, layer);
  if (!open) return null;
  return (
    <>
      <div className="s3-drawer-ov" onClick={onClose} />
      <aside
        ref={panel}
        className={className ? `s3-drawer ${className}` : 's3-drawer'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className={headClassName || undefined} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="s3-h3" style={{ flex: 1 }}>{title}</span>
          <button type="button" className="s3-pill s3-pill--icon" onClick={onClose} aria-label="Chiudi">
            <X size={15} />
          </button>
        </div>
        {children}
      </aside>
    </>
  );
};

/**
 * Modale V3. Porta `.adm2` sul nodo radice per due motivi: l'overlay fuori
 * dalla shell admin funziona solo nella forma COMPOSTA `.a2-overlay.adm2`
 * (`admin-v2.css:401-402`, dove `.adm2 .a2-overlay` copre il caso annidato),
 * e i token `--a2-*` sono dichiarati su `.adm2, .sup3`: entrambe le classi
 * servono quindi anche dentro l'area rappresentante. Il pannello NON usa
 * `.a2-modal` (che esiste solo come discendente, `admin-v2.css:413`) ma la
 * classe locale `.s3-modal` di support-v3.css.
 *
 * Nome accessibile obbligatorio: o `labelledBy` (id del titolo nel contenuto)
 * o `title` come fallback su `aria-label`. Nessuna modale anonima.
 */
type S3ModalProps = {
  open: boolean; onClose: () => void; children: React.ReactNode;
} & ({ labelledBy: string; title?: string } | { labelledBy?: undefined; title: string });

export const S3Modal: React.FC<S3ModalProps> = ({ open, onClose, labelledBy, title, children }) => {
  const panel = useRef<HTMLDivElement>(null);
  // Veste v4 (T38): la modale e sorella della radice `.sup4` della pagina, non
  // discendente, quindi porta `sup4` da se. Con la v3 la classe resta identica.
  const look = useSupportLook();
  const layer = useLayer(open, true);
  useEscape(open, layer, onClose);
  useFocusReturn(open, panel);
  useFocusTrap(open, panel, layer);
  if (!open) return null;
  return (
    <div className={supportRootClass('a2-overlay adm2 sup3', look)} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={panel}
        className="s3-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : title}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
};
